import { useState, useRef, useEffect, useId, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  IconButton,
  FormControl,
  Alert,
  AlertIcon,
  AlertDescription,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import { useAppSelector } from '@/store/store';
import { selectCurrentUserId } from '@/store/slices/authSlice';
import { MenuItemCard } from './MenuItemCard';
import { CourseTagSelector } from './CourseTagSelector';
import { ReportMenuItemModal } from './ReportMenuItemModal';
import { isDiscardable, type RatingMenuItem, type CourseType } from '../hooks/useRestaurantRating';
import { FOCUS_RING } from '../ratingCategories';
import { useDiscardUploads } from '@/api/mutations/useRestaurantMutations';
import { haptics } from '@/utils/haptics';

interface MenuItemsStepProps {
  /** Current menu items */
  menuItems: RatingMenuItem[];
  /** Add a new menu item */
  onAddItem: (name: string, course: CourseType) => void;
  /** Update a menu item */
  onUpdateItem: (id: string, updates: Partial<RatingMenuItem>) => void;
  /** Remove a menu item */
  onRemoveItem: (id: string) => void;
  /** Stage a picked photo on a menu item (rating hook state — pending, not yet uploaded to a server-side dish) */
  onAddMenuItemImage: (itemId: string, publicId: string, previewUrl: string) => void;
  /** Remove a staged photo from a menu item's rating hook state */
  onRemoveMenuItemImage: (itemId: string, publicId: string) => void;
  /** Go back to ratings page */
  onBack: () => void;
  /**
   * Finish and save ratings.
   * Returns a Promise — rejects on submission failure so this component can
   * surface the error inline and stay mounted. The parent advances the view
   * only on resolution (success path).
   */
  onFinish: () => Promise<void>;
  /** Restaurant ID — passed through to the report modal */
  restaurantId: number;
  /** Restaurant display name — passed through to the report modal */
  restaurantName: string;
}

interface AddDishCardProps {
  menuItems: RatingMenuItem[];
  onAddItem: (name: string, course: CourseType) => void;
}

/**
 * Inline form for adding a new dish — rendered as the final slide of the
 * carousel. Requires both a name AND a course selection before "Add dish"
 * enables; the course is persisted on creation rather than hardcoded.
 */
function AddDishCard({ menuItems, onAddItem }: AddDishCardProps) {
  const [dishName, setDishName] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<CourseType | null>(null);
  const [duplicateError, setDuplicateError] = useState(false);
  // Stable id for FormControl/FormLabel association
  const inputId = useId();

  // Check for duplicate names (case-insensitive).
  // TODO: consolidate with MenuItemCard.checkDuplicate
  const checkDuplicate = (name: string): boolean => {
    const normalizedName = name.trim().toLowerCase();
    return menuItems.some((item) => item.name.toLowerCase() === normalizedName);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDishName(value);
    // Check for duplicates as user types
    if (duplicateError || value.trim()) {
      setDuplicateError(checkDuplicate(value));
    }
  };

  const handleAddItem = () => {
    const trimmedName = dishName.trim();
    if (!trimmedName || !selectedCourse) return;

    if (checkDuplicate(trimmedName)) {
      setDuplicateError(true);
      haptics.error();
      return;
    }

    onAddItem(trimmedName, selectedCourse);
    setDishName('');
    setSelectedCourse(null);
    setDuplicateError(false);
    haptics.spring();
  };

  const canSubmitNewItem = dishName.trim().length > 0 && !duplicateError && selectedCourse !== null;

  return (
    <Box
      bg="whiteAlpha.200"
      borderRadius="var(--r-card-compact)"
      p={4}
      borderWidth="1px"
      borderColor="whiteAlpha.300"
      borderStyle="dashed"
      w="100%"
      minW={0}
      overflow="hidden"
    >
      <VStack spacing={4} align="stretch">
        {/* Item name input — FormControl + FormLabel for valid label association */}
        <FormControl isInvalid={duplicateError}>
          <Input
            id={inputId}
            placeholder="Enter item exactly as it appears on menu"
            value={dishName}
            onChange={handleNameChange}
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor={duplicateError ? 'red.500' : 'transparent'}
            color="white"
            _placeholder={{ color: 'gray.500' }}
            _focus={{
              bg: 'whiteAlpha.200',
              borderColor: duplicateError ? 'red.500' : 'purple.500',
              boxShadow: duplicateError ? '0 0 0 1px var(--chakra-colors-red-500)' : 'outline',
            }}
          />
          {duplicateError && (
            <Text color="red.400" fontSize="xs" mt={1}>
              A dish with this name already exists
            </Text>
          )}
        </FormControl>

        <Box>
          <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" mb={2}>
            Course
          </Text>
          <CourseTagSelector value={selectedCourse} onChange={setSelectedCourse} compact />
        </Box>

        {/* Add button */}
        <Button
          colorScheme="purple"
          onClick={handleAddItem}
          isDisabled={!canSubmitNewItem}
          leftIcon={<Plus size={16} />}
        >
          Add dish
        </Button>
      </VStack>
    </Box>
  );
}

/**
 * Menu items rating step — a Swiper carousel with one slide per rated dish
 * plus a final "add a dish" slide. Replaces the previous vertically
 * scrollable flat list; the whole menu is browsed one dish at a time.
 */
export function MenuItemsStep({
  menuItems,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onAddMenuItemImage,
  onRemoveMenuItemImage,
  onBack,
  onFinish,
  restaurantId,
  restaurantName,
}: MenuItemsStepProps) {
  // Get all menu item names for duplicate validation in each card
  const allNames = menuItems.map((item) => item.name);

  /**
   * Bounds the dish carousel to the same 560px column as the ratings booth
   * (RestaurantRatingModal.tsx) on desktop, so the layout stays coherent
   * when a guest moves from the booth into this step. Mobile stays
   * full-bleed. Same `md` breakpoint the booth uses for its own bound.
   */
  const isDesktop = useBreakpointValue({ base: false, md: true }, { ssr: false });
  const contentMaxW = isDesktop ? '560px' : '100%';

  const currentUserId = useAppSelector(selectCurrentUserId);

  // Local submission state — tracks the async onFinish call so we can
  // re-enable the button on failure and show an error without unmounting.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Report modal state — a single modal instance is shared across all cards
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState<number | undefined>(undefined);

  const { discardUploads } = useDiscardUploads();

  const swiperRef = useRef<SwiperType | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  // Tracks the previous menuItems.length so the slide-to-newest effect below
  // fires only when the list GROWS (an add), never on a same-or-shrinking
  // change (a remove) — a remove should leave the carousel where it is.
  const prevMenuItemsLengthRef = useRef(menuItems.length);

  useEffect(() => {
    if (menuItems.length > prevMenuItemsLengthRef.current) {
      // Swiper's own docs: call update() "after you add/remove slides
      // manually, or ... do any custom DOM modifications with Swiper" —
      // forces a synchronous, guaranteed-fresh slide-size/snapGrid
      // recalculation right before slideTo() targets the newly-added slide.
      // Without this, slideTo() can compute its translate against a stale
      // grid and produce a garbage off-screen position.
      swiperRef.current?.update();
      swiperRef.current?.slideTo(menuItems.length - 1);
    }
    prevMenuItemsLengthRef.current = menuItems.length;
  }, [menuItems.length]);

  const handleReport = (menuItemId: number) => {
    setSelectedMenuItemId(menuItemId);
    setIsReportOpen(true);
  };

  const handleRemoveItem = useCallback(
    (item: RatingMenuItem) => {
      const discardableIds = item.pendingImages
        .filter((img) => isDiscardable(img.status))
        .map((img) => img.publicId);
      if (discardableIds.length > 0) {
        discardUploads(discardableIds);
      }
      onRemoveItem(item.id);
    },
    [onRemoveItem, discardUploads]
  );

  const handleFinish = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onFinish();
      // Parent advances the view on success — nothing to do here
    } catch (err) {
      // Stay mounted; surface the error inline
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmitError(message);
      haptics.error();
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSlides = menuItems.length + 1; // + the trailing AddDishCard slide

  return (
    <Box display="flex" flexDirection="column" h="100%">
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        minW={0}
        w="100%"
        maxW={contentMaxW}
        mx="auto"
      >
        {/* Scrollable content */}
        <Box
          flex="1"
          minW={0}
          overflowY="auto"
          overflowX="hidden"
          px={4}
          pt={4}
          pb="80px"
          sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
        >
          <VStack spacing={4} align="stretch" w="100%" minW={0}>
            {/* Header */}
            <VStack spacing={2} textAlign="center">
              <Text fontSize="3xl">🍽️</Text>
              <Heading size="lg" color="white" fontWeight="bold">
                What did you try?
              </Heading>
            </VStack>

            {/* Slide counter + prev/next nav */}
            <HStack justify="space-between">
              <Text color="gray.400" fontSize="sm" fontWeight="medium">
                Your dishes ({menuItems.length})
              </Text>
              <HStack spacing={1}>
                <IconButton
                  aria-label="Previous dish"
                  icon={<ChevronLeft size={18} />}
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  isDisabled={activeSlideIndex === 0}
                  onClick={() => swiperRef.current?.slidePrev()}
                  _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                  _focusVisible={FOCUS_RING}
                />
                <IconButton
                  aria-label="Next dish"
                  icon={<ChevronRight size={18} />}
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  isDisabled={activeSlideIndex >= totalSlides - 1}
                  onClick={() => swiperRef.current?.slideNext()}
                  _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                  _focusVisible={FOCUS_RING}
                />
              </HStack>
            </HStack>

            <Swiper
              data-testid="rating-menu-swiper"
              // minWidth: 0 overrides the flex item's default `min-width: auto`.
              // Without it, a slide's unwrapped intrinsic content (e.g.
              // AddDishCard's 13-button CourseTagSelector row) can drive this
              // flex child's measured width up without bound — Chromium clamps
              // the runaway result to its internal max layout value (2^25px),
              // which is what was making every slide's contents unreachable.
              style={{ minWidth: 0, width: '100%' }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
              spaceBetween={16}
              slidesPerView={1}
            >
              {menuItems.map((item) => (
                <SwiperSlide key={item.id}>
                  <MenuItemCard
                    item={item}
                    existingNames={allNames}
                    onRemove={() => handleRemoveItem(item)}
                    onNameChange={(name) => onUpdateItem(item.id, { name })}
                    onRatingChange={(rating) => onUpdateItem(item.id, { rating })}
                    onReport={handleReport}
                    onImageUpload={(publicId, previewUrl) => onAddMenuItemImage(item.id, publicId, previewUrl)}
                    onImageRemove={(publicId) => onRemoveMenuItemImage(item.id, publicId)}
                    ownerId={currentUserId}
                  />
                </SwiperSlide>
              ))}
              <SwiperSlide data-testid="rating-add-dish-slide">
                <AddDishCard menuItems={menuItems} onAddItem={onAddItem} />
              </SwiperSlide>
            </Swiper>

            {submitError && (
              <Alert status="error" borderRadius="var(--r-inner)">
                <AlertIcon />
                <AlertDescription fontSize="sm">{submitError}</AlertDescription>
              </Alert>
            )}
          </VStack>
        </Box>

        {/* Sticky footer */}
        <Box
          position="sticky"
          bottom={0}
          borderTop="1px solid"
          borderColor="whiteAlpha.200"
          bg="gray.900"
          px={4}
          py={3}
          height="72px"
          display="flex"
          alignItems="center"
          gap={3}
          zIndex={1}
          pb="calc(12px + env(safe-area-inset-bottom, 0px))"
        >
          <Button
            variant="ghost"
            size="lg"
            color="gray.400"
            onClick={onBack}
            aria-label="Back to ratings"
            _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
            _focusVisible={FOCUS_RING}
          >
            <ChevronLeft size={20} />
            Back
          </Button>
          <Button
            colorScheme="purple"
            size="lg"
            onClick={handleFinish}
            isLoading={isSubmitting}
            loadingText="Saving..."
            flex={1}
            data-testid="rating-submit"
            _focusVisible={FOCUS_RING}
          >
            Finish
          </Button>
        </Box>
      </Box>

      {selectedMenuItemId !== undefined && (
        <ReportMenuItemModal
          isOpen={isReportOpen}
          onClose={() => setIsReportOpen(false)}
          menuItemId={selectedMenuItemId}
          restaurantId={restaurantId}
          restaurantName={restaurantName}
        />
      )}
    </Box>
  );
}

export default MenuItemsStep;
